import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import spaceship from '../../assets/spaceship/webp/final_cover.mp4'; 

// Animated gradient background
const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const HeroContainer = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 100px;
  background: linear-gradient(135deg, #1e1e1e 0%, #2a1a3d 50%, #1e1e1e 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 15s ease infinite;
  color: #fff;
  overflow: hidden;
  font-family: 'RobotoMono', sans-serif;
  position: relative;

  @media (max-width: 768px) { padding-top: 90px; }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
  }

  @media (min-width: 768px) { flex-direction: row; }
`;

const LeftContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px;
  text-align: left;
  margin-top: -5%;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    padding: 24px;
    margin-top: 0;
    align-items: center;
    text-align: center;
  }

  @media (min-width: 768px) {
    flex: 0 0 45%;
    padding: 60px 80px;
  }
`;

const Headline = styled.h1`
  font-size: 1.25em;
  font-weight: 400;
  color: #ffffff;
  margin-bottom: 0.5em;
  letter-spacing: 0.05em;

  @media (min-width: 768px) { font-size: 1.5em; }
`;

const GradientText = styled.h2`
  background: linear-gradient(135deg, #a855f7 0%, #d4a1ff 50%, #f0abfc 100%);
  background-size: 200% 200%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: clamp(2.5em, 8vw, 5em);
  font-weight: 700;
  margin: 0.1em 0;
  letter-spacing: -0.02em;
  line-height: 1.1;
  animation: ${gradientShift} 8s ease infinite;
  filter: drop-shadow(0 4px 12px rgba(138, 43, 226, 0.3));
`;

const TypewriterText = styled.div`
  color: #d4a1ff;
  font-size: clamp(1.1em, 3vw, 1.5em);
  margin-top: 0.5em;
  min-height: 1.5em;
  font-weight: 500;
  
  &::after {
    content: '_';
    animation: ${keyframes`0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; }`} 1s infinite;
    color: #a855f7;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 2.5em;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;

const ConnectBtn = styled.button`
  padding: 14px 32px;
  background: linear-gradient(135deg, rgba(168, 85, 247, 0.2) 0%, rgba(212, 161, 255, 0.1) 100%);
  backdrop-filter: blur(15px);
  border-radius: 50px;
  border: 1px solid rgba(168, 85, 247, 0.4);
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(138, 43, 226, 0.2);
  width: fit-content;
  white-space: nowrap;

  &:hover {
    transform: translateY(-3px);
    background: rgba(168, 85, 247, 0.3);
    border-color: #d4a1ff;
    box-shadow: 0 12px 30px rgba(138, 43, 226, 0.4);
  }
`;

const RightContainer = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  min-height: 50vh;

  @media (min-width: 768px) {
    flex: 0 0 55%;
    justify-content: flex-start;
    padding-left: clamp(20px, 3vw, 56px);
  }
`;



const Spaceship = styled.video`
  width: 80%;
  z-index: 1;

  @media (min-width: 768px) {
    width: 62%;
  }
`;


const typewriterTexts = [
  "Software Engineer @ League",
  "Computer Science Student @ York University",
  "Tech Enthusiast",
  "Problem Solver",
];

const Hero: React.FC = () => {
  const [currentText, setCurrentText] = useState('');

  useEffect(() => {
    let textIndex = 0;
    let characterIndex = 0;
    let isDeleting = false;
    let timeoutId: ReturnType<typeof setTimeout>;

    const tick = () => {
      const currentTitle = typewriterTexts[textIndex];
      setCurrentText(currentTitle.slice(0, characterIndex));

      if (!isDeleting && characterIndex < currentTitle.length) {
        characterIndex += 1;
        timeoutId = setTimeout(tick, 100);
        return;
      }

      if (!isDeleting && characterIndex === currentTitle.length) {
        isDeleting = true;
        timeoutId = setTimeout(tick, 1400);
        return;
      }

      if (isDeleting && characterIndex > 0) {
        characterIndex -= 1;
        timeoutId = setTimeout(tick, 50);
        return;
      }

      isDeleting = false;
      textIndex = (textIndex + 1) % typewriterTexts.length;
      timeoutId = setTimeout(tick, 400);
    };

    timeoutId = setTimeout(tick, 0);

    return () => clearTimeout(timeoutId);
  }, []);

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <HeroContainer id="home">
      <LeftContainer>
        <Headline>Hey there!</Headline>
        <GradientText>I'm Carmella Setiabudi</GradientText>
        <TypewriterText>{currentText}</TypewriterText>
        
        <ButtonGroup>
          <ConnectBtn onClick={() => scrollToSection('connect')}>
            Connect with me
          </ConnectBtn>
          <ConnectBtn onClick={() => scrollToSection('projects')}>
            View my work
          </ConnectBtn>
        </ButtonGroup>
      </LeftContainer>

      <RightContainer>
        <Spaceship
  autoPlay
  loop
  muted
  playsInline
>
  <source src={spaceship} type="video/mp4" />
</Spaceship>

        
      </RightContainer>
    </HeroContainer>
  );
};

export default Hero;
